import { ComponentType, FC, ReactElement } from 'react';

import moment, { Moment } from 'moment';

import { CalendarHolidaysViewModel } from '@api/models/CalendarHolidaysViewModel';
import { CalendarHolidayItemViewModel } from '@api/models/CalendarHolidayItemViewModel';
import { URL_DATE_FORMAT, convertDateToMomentObject } from '@utils/dateUtil';

import {
  HolidayDayContent,
  HolidayDot,
  WeekHolidayDot,
} from './DatePickerStyle';

/**
 * Holiday calendar helpers (MAP-takvim-guncelleme / GSF-WEB-005).
 *
 * Port of Setur's `buildHolidayDateSet()` / `renderCalendarEventFast()` logic:
 * the CMS (Kentico) driven holiday payload is flattened once into a date-keyed
 * `Map` so that each calendar cell can look up its holiday in O(1) instead of
 * scanning the full list on every render.
 *
 * These are pure helpers only. Wiring into `DateRangePicker`/`SingleDatePicker`
 * (GSF-WEB-003/004) and the `Holiday`/`WeekHoliday` styled-components
 * (GSF-WEB-006) are handled in their own tasks; the render helper receives the
 * marker components via injection so it stays style-agnostic.
 */

export type HolidayCalendarType = 'holiday' | 'weekend';

export interface HolidayLookupEntry {
  /** Whether this day comes from the official holiday or the weekend list. */
  type: HolidayCalendarType;
  /** The originating CMS item (redirected dates, description, etc.). */
  item: CalendarHolidayItemViewModel;
}

/** Date key (`YYYY-MM-DD`) → holiday lookup entry. */
export type HolidayDateMap = Map<string, HolidayLookupEntry>;

/**
 * Safety cap on how many days a single holiday item may span while expanding a
 * range, guarding against malformed CMS data (e.g. an end date far in the future
 * or reversed start/end) causing a runaway loop.
 */
const MAX_HOLIDAY_SPAN_DAYS = 366;

// Kentico holiday payload stores Turkey-local midnight as a UTC timestamp
// (e.g. `2026-07-24T21:00:00Z` for 25 Temmuz 00:00 TRT). Normalize those ISO
// values back to Turkey-local day boundaries before building the calendar map.
const HOLIDAY_SOURCE_UTC_OFFSET_HOURS = 3;

export const parseHolidayPayloadDate = (
  date?: string | null,
  fallbackDateFormat: string = URL_DATE_FORMAT
): Moment => {
  if (!date) return convertDateToMomentObject(undefined, fallbackDateFormat);

  return date.includes('T')
    ? moment.utc(date).add(HOLIDAY_SOURCE_UTC_OFFSET_HOURS, 'hours')
    : convertDateToMomentObject(date, fallbackDateFormat);
};

const expandHolidayItem = (
  item: CalendarHolidayItemViewModel,
  type: HolidayCalendarType,
  target: HolidayDateMap
): void => {
  const start = parseHolidayPayloadDate(item.holidayStartDate, URL_DATE_FORMAT);
  const end = parseHolidayPayloadDate(
    item.holidayEndDate ?? item.holidayStartDate,
    URL_DATE_FORMAT
  );

  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return;

  const cursor = start.clone();
  let guard = 0;

  while (cursor.isSameOrBefore(end, 'day') && guard <= MAX_HOLIDAY_SPAN_DAYS) {
    const key = cursor.format(URL_DATE_FORMAT);

    // Official holidays take precedence over weekend holidays on overlap, so a
    // weekend entry never overwrites an already-registered official holiday.
    if (!target.has(key)) target.set(key, { type, item });

    cursor.add(1, 'day');
    guard += 1;
  }
};

/**
 * Flattens the CMS holiday payload into a date-keyed `Map` for O(1) lookups.
 *
 * Official holidays (`calendarHoliday`) are registered first so they win over
 * overlapping weekend holidays (`weekendHoliday`).
 */
export const buildHolidayDateSet = (
  data?: CalendarHolidaysViewModel | null
): HolidayDateMap => {
  const holidayMap: HolidayDateMap = new Map();

  if (!data) return holidayMap;

  data.calendarHoliday?.forEach((item) =>
    expandHolidayItem(item, 'holiday', holidayMap)
  );
  data.weekendHoliday?.forEach((item) =>
    expandHolidayItem(item, 'weekend', holidayMap)
  );

  return holidayMap;
};

/**
 * O(1) lookup of the holiday entry for a given calendar day.
 */
export const getHolidayForDate = (
  day: Moment,
  holidayMap: HolidayDateMap
): HolidayLookupEntry | undefined =>
  holidayMap.get(day.format(URL_DATE_FORMAT));

/**
 * Marker components rendered on top of a holiday cell. Injected by the caller
 * (GSF-WEB-003/004) using the styled-components added in GSF-WEB-006, keeping
 * this render helper free of styling concerns.
 */
export interface HolidayMarkerComponents {
  Holiday: ComponentType<{ item: CalendarHolidayItemViewModel }>;
  WeekHoliday: ComponentType<{ item: CalendarHolidayItemViewModel }>;
}

/**
 * Official-holiday (Resmi Tatil) per-cell dot marker (MAP-takvim-guncelleme).
 *
 * The small ~4px coral dot beneath the day number (Figma Brand/BR300 #FA5851,
 * Setur reference: `CalenderEventDay`). Weekend holidays use a distinct teal
 * marker ({@link WeekHolidayDayMarker}); precedence on overlap is resolved
 * upstream in `buildHolidayDateSet`.
 */
export const HolidayDayMarker: FC<{
  item: CalendarHolidayItemViewModel;
}> = ({ item }) => <HolidayDot title={item.holidayName ?? undefined} />;

/**
 * Weekend-holiday (Hafta Sonu) per-cell dot marker (MAP-takvim-guncelleme).
 *
 * Same 4px dot as {@link HolidayDayMarker} but in Figma Teal/T400 (#00A3BF) so
 * weekend holidays are visually distinguishable from official holidays.
 */
export const WeekHolidayDayMarker: FC<{
  item: CalendarHolidayItemViewModel;
}> = ({ item }) => <WeekHolidayDot title={item.holidayName ?? undefined} />;

/**
 * Ready-to-inject marker set matching {@link HolidayMarkerComponents}. Passed to
 * `renderCalendarEventFast` by both pickers. Official holidays render the coral
 * marker, weekend holidays the teal one.
 */
export const holidayCellMarkers: HolidayMarkerComponents = {
  Holiday: HolidayDayMarker,
  WeekHoliday: WeekHolidayDayMarker,
};

/**
 * Renders a single calendar day's contents (day number + holiday marker) using
 * the pre-built O(1) `holidayMap`. Suitable for `react-dates`
 * `renderDayContents`.
 */
export const renderCalendarEventFast = (
  day: Moment,
  holidayMap: HolidayDateMap,
  components: HolidayMarkerComponents
): ReactElement => {
  const entry = getHolidayForDate(day, holidayMap);

  const Marker =
    entry?.type === 'weekend'
      ? components.WeekHoliday
      : entry?.type === 'holiday'
      ? components.Holiday
      : null;

  return (
    <HolidayDayContent>
      {day.date()}
      {Marker && entry ? <Marker item={entry.item} /> : null}
    </HolidayDayContent>
  );
};
